// The market's one database client and function caller.
//
//   • production by default — the same Supabase project as the rest of the app;
//   • the LOCAL test stack when REACT_APP_MARKET_API_URL is set (scripts/hiobuy/
//     local-stack: embedded Postgres + PostgREST + the real market-checkout
//     handler). The user's own production session is passed as the token; the
//     local PostgREST verifies it against the project's public keys. Never set
//     in Vercel.

import { createClient } from '@supabase/supabase-js';
import { sb, SUPABASE_ANON_KEY, SUPABASE_FUNCTIONS_URL } from '../supabase';

const LOCAL_API = process.env.REACT_APP_MARKET_API_URL || '';
export const MARKET_API_IS_LOCAL = !!LOCAL_API;

export const marketToken = async () => (await sb.auth.getSession()).data.session?.access_token ?? null;
export const marketDb = LOCAL_API ? createClient(LOCAL_API, SUPABASE_ANON_KEY, { accessToken: marketToken }) : sb;
// A Vercel preview build talks to market-checkout-preview (point 2, owner 2026-11-02: everything is tested on
// a preview before one release) — the same code deployed under its own name; production keeps market-checkout.
// REACT_APP_MARKET_CHECKOUT_FN overrides both.
const PREVIEW_HOST = typeof window !== 'undefined' && /\.vercel\.app$/i.test(window.location.hostname);
export const MARKET_CHECKOUT_FN = process.env.REACT_APP_MARKET_CHECKOUT_FN || (PREVIEW_HOST ? 'market-checkout-preview' : 'market-checkout');
const FN_URL = `${LOCAL_API ? `${LOCAL_API}/functions/v1` : SUPABASE_FUNCTIONS_URL}/${LOCAL_API ? 'market-checkout' : MARKET_CHECKOUT_FN}`;

/**
 * market-checkout edge function. Signed-in, except 'quote' and 'cart_options': a visitor may
 * calculate the total before signing in (guest checkout — the server decides
 * whether that is switched on, and limits it).
 */
export async function marketFn(body) {
  const token = (await marketToken()) || (['quote', 'cart_options'].includes(body?.action) ? SUPABASE_ANON_KEY : null);
  if (!token) return { ok: false, error: 'not_signed_in' };
  try {
    const r = await fetch(FN_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    return { httpStatus: r.status, ...j };
  } catch (e) {
    return { ok: false, error: 'network_error' };
  }
}

/** market-image-search: search by photo. Open to visitors (rate-limited server-side). */
export async function marketImageSearchFn(body) {
  const token = (await marketToken()) || SUPABASE_ANON_KEY;
  try {
    const r = await fetch(`${LOCAL_API ? `${LOCAL_API}/functions/v1` : SUPABASE_FUNCTIONS_URL}/market-image-search`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    return { httpStatus: r.status, ...j };
  } catch (e) {
    return { ok: false, error: 'network_error' };
  }
}

/** market-source-search: photo and text search on HioBuy's sources, and opening a result.
 *  A visitor calls with the public key and is limited by address on the server (owner decision 2026-10-22). */
export async function marketSourceSearchFn(body) {
  const token = (await marketToken()) || SUPABASE_ANON_KEY;
  try {
    const r = await fetch(`${LOCAL_API ? `${LOCAL_API}/functions/v1` : SUPABASE_FUNCTIONS_URL}/market-source-search`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    return { httpStatus: r.status, ...j };
  } catch (e) {
    return { ok: false, error: 'network_error' };
  }
}
/** A photo-search result's picture, served by market-image under an opaque ref (never the source address). */
export const sourceImageUrl = (ref) => `${LOCAL_API ? `${LOCAL_API}/functions/v1` : SUPABASE_FUNCTIONS_URL}/market-image/${ref}`;

export const marketRpc = async (name, args) => {
  const { data, error } = await marketDb.rpc(name, args);
  return error ? { ok: false, error: error.message } : { ok: true, data };
};
